import React, { useContext, useState } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { TRANSPORT_MODES } from '../services/fbaInboundApi';
import { Tag, Truck, Package, CheckCircle, Loader2, Plus, Trash2 } from 'lucide-react';

const Step6_TrackingDetails = () => {
  const {
    transportMode,
    selectedPlacementOption,
    updateTracking,
    trackingDetails,
    nextStep,
    loading
  } = useContext(FBAInboundContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(Object.keys(trackingDetails).length > 0);

  // LTL tracking state
  const [bolNumber, setBolNumber] = useState('');
  const [carrier, setCarrier] = useState('');

  // SPD tracking state
  const [boxTrackingNumbers, setBoxTrackingNumbers] = useState([
    { boxId: '', trackingNumber: '' }
  ]);

  const isLTL = transportMode === TRANSPORT_MODES.NON_PARTNERED_LTL;
  const isPartneredSPD = transportMode === TRANSPORT_MODES.PARTNERED_SPD;

  // Get boxes from placement option
  const boxes = selectedPlacementOption?.shipments?.flatMap(s => s.boxes || []) || [];

  const handleAddTrackingRow = () => {
    setBoxTrackingNumbers(prev => [...prev, { boxId: '', trackingNumber: '' }]);
  };

  const handleRemoveTrackingRow = (index) => {
    setBoxTrackingNumbers(prev => prev.filter((_, i) => i !== index));
  };

  const handleTrackingChange = (index, field, value) => {
    setBoxTrackingNumbers(prev => prev.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    ));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (isLTL) {
        await updateTracking({
          bill_of_lading_number: bolNumber,
          carrier_name: carrier
        });
      } else if (!isPartneredSPD) {
        // Non-partnered SPD
        const trackingData = {
          tracking_items: boxTrackingNumbers
            .filter(row => row.boxId && row.trackingNumber)
            .map(row => ({
              box_id: row.boxId,
              tracking_id: row.trackingNumber
            }))
        };
        await updateTracking(trackingData);
      }

      setSuccess(true);
      setTimeout(() => {
        nextStep();
      }, 1500);
    } catch (err) {
      console.error('Failed to update tracking:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // For partnered SPD, Amazon auto-assigns tracking
  if (isPartneredSPD) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Tracking Auto-Assigned
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Since you selected Partnered Small Parcel, Amazon will automatically
            assign tracking numbers to your boxes.
          </p>
        </div>

        <button
          onClick={nextStep}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 flex items-center justify-center"
        >
          Continue to Labels
          <Tag className="w-5 h-5 ml-2" />
        </button>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Tracking Details Saved
        </h3>
        <p className="text-gray-600">
          Your tracking information has been recorded.
        </p>
      </div>
    );
  }

  // LTL Form
  if (isLTL) {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Truck className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-blue-900">LTL Tracking Details</h4>
              <p className="text-sm text-blue-700 mt-1">
                Enter your Bill of Lading number and carrier information for this LTL shipment.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bill of Lading Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bolNumber}
              onChange={(e) => setBolNumber(e.target.value)}
              placeholder="Enter BOL number"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Carrier Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="e.g., FedEx Freight, UPS Freight"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!bolNumber || !carrier || isSubmitting || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Tag className="w-5 h-5 mr-2" />
              Save Tracking Details
            </>
          )}
        </button>
      </div>
    );
  }

  // Non-Partnered SPD Form
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Package className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">Small Parcel Tracking</h4>
            <p className="text-sm text-blue-700 mt-1">
              Enter tracking numbers for each box in your shipment.
            </p>
          </div>
        </div>
      </div>

      {/* Tracking Entries */}
      <div className="space-y-3">
        {boxTrackingNumbers.map((row, index) => (
          <div key={index} className="flex items-center space-x-3">
            <div className="flex-1">
              <input
                type="text"
                value={row.boxId}
                onChange={(e) => handleTrackingChange(index, 'boxId', e.target.value)}
                placeholder="Box ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-[2]">
              <input
                type="text"
                value={row.trackingNumber}
                onChange={(e) => handleTrackingChange(index, 'trackingNumber', e.target.value)}
                placeholder="Tracking Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {boxTrackingNumbers.length > 1 && (
              <button
                onClick={() => handleRemoveTrackingRow(index)}
                className="p-2 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={handleAddTrackingRow}
        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Another Box
      </button>

      {/* Available Boxes Reference */}
      {boxes.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Available Box IDs:</h4>
          <div className="flex flex-wrap gap-2">
            {boxes.map((box, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
              >
                {box.amazon_box_id || box.box_number || `Box ${idx + 1}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={boxTrackingNumbers.every(r => !r.trackingNumber) || isSubmitting || loading}
        className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Tag className="w-5 h-5 mr-2" />
            Save Tracking Details
          </>
        )}
      </button>
    </div>
  );
};

export default Step6_TrackingDetails;
